import React, { useState } from 'react';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import ClickableStack from 'talent_matching/components/shared/clickable_stack';
import Collapsible from '@grnhse/seedling/lib/birch/components/collapsible';
import ArrowUpIcon from '@grnhse/seedling/lib/birch/icons/arrow_up';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/arrow_down';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import { useApplicationHistory } from 'talent_matching/api';
import { withLocalePrefix } from 'shared/utils/translation';
import RediscoveryApplicationCard from './rediscovery_application_card';
import type {
  RediscoveryApplicationHistory as RediscoveryApplicationHistoryType,
  RediscoveryApplicationHistoryItem,
  Document,
} from 'talent_matching/types';

const t = withLocalePrefix(
  'talent_matching.panel.rediscovery_application_history'
);

type Props = {
  historyUrl: string | undefined;
  activeDocument?: Document | null;
  onOpenDocument?: (document: Document) => void;
  canDownloadCandidateData?: boolean;
};

type CollapsibleSectionProps = {
  id: string;
  title: string;
  applications: RediscoveryApplicationHistoryItem[];
  defaultOpen?: boolean;
  activeDocument?: Document | null;
  onOpenDocument?: (document: Document) => void;
  canDownloadCandidateData?: boolean;
};

const CollapsibleSection = ({
  id,
  title,
  applications,
  defaultOpen = true,
  activeDocument,
  onOpenDocument,
  canDownloadCandidateData,
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (applications.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <ClickableStack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Body medium>{title}</Body>
        {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
      </ClickableStack>
      <Collapsible id={id} isOpen={isOpen}>
        <Stack spacing={2}>
          {applications.map((app) => (
            <RediscoveryApplicationCard
              key={app.id}
              application={app}
              activeDocument={activeDocument}
              onOpenDocument={onOpenDocument}
              canDownloadCandidateData={canDownloadCandidateData}
            />
          ))}
        </Stack>
      </Collapsible>
    </Stack>
  );
};

const RediscoveryApplicationHistory = ({
  historyUrl,
  activeDocument,
  onOpenDocument,
  canDownloadCandidateData = false,
}: Props) => {
  const { data, isLoading, isFetching, error, refetch } = useApplicationHistory(
    historyUrl as string
  );

  if (isLoading || isFetching) {
    return <SkeletonLoader columnsPerRow={1} rows={5} />;
  }

  if (error) {
    return (
      <Stack>
        <Body>{t('error')}</Body>
        <SecondaryButton onClick={() => refetch()} size="md">
          {t('retry')}
        </SecondaryButton>
      </Stack>
    );
  }

  const applicationHistory: RediscoveryApplicationHistoryType = data;

  return (
    <Stack spacing={3}>
      <Body medium>{t('title')}</Body>

      <CollapsibleSection
        id="active-applications"
        title={t('active_applications')}
        applications={applicationHistory.activeApplications}
        activeDocument={activeDocument}
        onOpenDocument={onOpenDocument}
        canDownloadCandidateData={canDownloadCandidateData}
      />

      <CollapsibleSection
        id="previous-applications"
        title={t('previous_applications')}
        applications={applicationHistory.previousApplications}
        activeDocument={activeDocument}
        onOpenDocument={onOpenDocument}
        canDownloadCandidateData={canDownloadCandidateData}
      />
    </Stack>
  );
};

export default RediscoveryApplicationHistory;
